import { BellOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons'
import { Badge, Button, Drawer, Empty, Skeleton, Space, Tooltip } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useMarkAllNotificationsRead,
  useNotifications,
  useUnreadNotificationCount,
} from '../../hooks/useNotifications'

import { DesktopPushRow } from './DesktopPushRow'
import { NotificationItem } from './NotificationItem'

import './DesktopPushRow.css'

interface Props {
  /** Width of the side drawer. */
  width?: number | string
  /** «pill»: زرّ نصي بعدّاد أزرق (ثيم «ستوديو») بدل الجرس الدائري */
  variant?: 'icon' | 'pill'
}

/**
 * Bell icon in the header with an unread Badge. Click opens a side Drawer
 * with the latest 30 notifications, supports mark-all-read, and refreshes
 * automatically via SignalR (see useNotifications).
 *
 * Mount once in AppLayout. Reads + invalidations are scoped by TanStack
 * Query keys (`queryKeys.notifications.*`) so other components (e.g. the
 * Dashboard ActivityFeed) can share the same data without duplicate fetches.
 */
export function NotificationCenter({ width = 380, variant = 'icon' }: Props) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  const { data: unreadCount = 0 } = useUnreadNotificationCount()
  const { data, isLoading, refetch, isFetching } = useNotifications({ take: 30 })
  const markAll = useMarkAllNotificationsRead()

  const items = data?.items ?? []
  const isRTL = i18n.language === 'ar'

  return (
    <>
      {variant === 'pill' ? (
        <button
          type="button"
          className="st-pill-btn"
          onClick={() => setOpen(true)}
          aria-label={t('notifications.title', 'الإشعارات')}
        >
          <BellOutlined />
          <span className="st-pill-label">{t('notifications.title', 'الإشعارات')}</span>
          {unreadCount > 0 && (
            <span className="st-pill-count">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      ) : (
        <Tooltip title={t('notifications.title', 'الإشعارات')}>
          <Badge count={unreadCount} overflowCount={99} offset={[-4, 4]}>
            <Button
              type="text"
              shape="circle"
              icon={<BellOutlined />}
              size="large"
              onClick={() => setOpen(true)}
              aria-label={t('notifications.title', 'الإشعارات')}
            />
          </Badge>
        </Tooltip>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width={width}
        placement={isRTL ? 'left' : 'right'}
        styles={{ body: { padding: 0 } }}
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span style={{ fontWeight: 700 }}>{t('notifications.title', 'الإشعارات')}</span>
            <Space size={4}>
              <Tooltip title={t('common.refresh', 'تحديث')}>
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined spin={isFetching} />}
                  onClick={() => refetch()}
                />
              </Tooltip>
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                disabled={unreadCount === 0 || markAll.isPending}
                loading={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                {t('notifications.markAllRead', 'تحديد الكل كمقروء')}
              </Button>
            </Space>
          </div>
        }
      >
        <DesktopPushRow />
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {isLoading ? (
            <>
              <Skeleton active paragraph={{ rows: 1 }} />
              <Skeleton active paragraph={{ rows: 1 }} />
              <Skeleton active paragraph={{ rows: 1 }} />
            </>
          ) : items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('notifications.empty', 'لا توجد إشعارات بعد')}
              style={{ padding: '40px 0' }}
            />
          ) : (
            items.map((n) => (
              <NotificationItem key={n.id} notification={n} onAfterClick={() => setOpen(false)} />
            ))
          )}
        </div>
      </Drawer>
    </>
  )
}

export default NotificationCenter
